<template>
  <div class="breathing-exercises" :style="containerStyle">
    <!-- AI Loading Overlay -->
    <div v-if="aiLoading" class="ai-loading-overlay">
      <div class="ai-loading-content">
        <div class="spinner"></div>
        <p class="ai-loading-text">Optimizing layout...</p>
        <p class="ai-loading-subtext">AI is analyzing your screen</p>
      </div>
    </div>

    <h1 class="page-title" :style="titleStyle">Take a moment to:</h1>

    <div v-if="loading" class="loading">Loading exercises...</div>

    <div v-else-if="error" class="error">{{ error }}</div>

    <div v-else class="courses-grid" :style="gridStyle">
      <div v-for="(exercise, index) in limitedExercises" :key="exercise.id" class="course-item" :style="{ animationDelay: `${index * 0.1}s` }">
        <ExerciseCard :exercise="exercise" @click="openModal(exercise)" />
      </div>
    </div>

    <VideoModal
      :exercise="selectedExercise"
      :isOpen="isModalOpen"
      @close="closeModal"
    />
  </div>
</template>

<script>
// Vue 3 Composition API imports - used in setup()
import ExerciseCard from '@/components/ExerciseCard.vue'
import VideoModal from '@/components/VideoModal.vue'
import { getBreathingExercises } from '@/services/api'
import { useAILayout } from '@/composables/useAILayout'

const defaultCourses = [
  {
    id: 1,
    main_title: 'CALM DOWN',
    subtitle: '',
    styleType: 'calm',
    video_url: 'https://kewaar2026transcoded.media-storage.eu-central.qencode.com/CALM%20DOWN%20VERTICAL/playlist.m3u8'
  },
  {
    id: 2,
    main_title: 'RELAX',
    subtitle: '',
    styleType: 'relax',
    video_url: 'https://kewaar2026transcoded.media-storage.eu-central.qencode.com/PANIC%20ATTACK%20VERTICAL/playlist.m3u8'
  },
  {
    id: 3,
    main_title: 'RECHARGE',
    subtitle: '',
    styleType: 'recharge',
    video_url: 'https://kewaar2026transcoded.media-storage.eu-central.qencode.com/RECHARGE%20VERTICAL/playlist.m3u8'
  },
  {
    id: 4,
    main_title: 'BOOST',
    subtitle: '',
    styleType: 'boost',
    video_url: 'https://kewaar2026transcoded.media-storage.eu-central.qencode.com/BOOST%20VERTICAL/playlist.m3u8'
  }
]

export default {
  name: 'BreathingExercises',
  components: {
    ExerciseCard,
    VideoModal
  },
  setup() {
    // Use AI-powered layout
    const { 
      isLoading: aiLoading, 
      error: aiError,
      containerStyle, 
      titleStyle, 
      gridStyle,
      refreshLayout,
      forceRefresh
    } = useAILayout()

    // Expose to window for console access
    if (typeof window !== 'undefined') {
      window.aiLayout = {
        refresh: refreshLayout,
        forceRefresh: forceRefresh
      }
    }

    return {
      aiLoading,
      aiError,
      containerStyle,
      titleStyle,
      gridStyle
    }
  },
  data() {
    return {
      exercises: [],
      loading: true,
      error: null,
      selectedExercise: null,
      isModalOpen: false
    }
  },
  computed: {
    limitedExercises() {
      return this.exercises.slice(0, 4)
    }
  },
  async mounted() {
    await this.fetchExercises()
  },
  methods: {
    async fetchExercises() {
      try {
        this.loading = true
        this.error = null
        
        // Fetch from API
        const response = await getBreathingExercises()
        if (response.data && response.data.length > 0) {
          this.exercises = response.data.map((video, index) => ({
            id: video.id || index + 1,
            main_title: video.main_title || video.title || 'Video ' + (index + 1),
            subtitle: '',
            styleType: ['calm', 'relax', 'recharge', 'boost'][index % 4],
            video_url: video.video_url || video.videoUrl || '',
            image_url: video.image_url || ''
          }))
        } else {
          this.exercises = [...defaultCourses]
        }
      } catch (err) {
        this.error = 'Failed to load exercises.'
        console.error('Error loading exercises:', err)
        // Fallback to defaults
        this.exercises = [...defaultCourses]
      } finally {
        this.loading = false
      }
    },
    openModal(exercise) {
      this.selectedExercise = exercise
      this.isModalOpen = true
    },
    closeModal() {
      this.isModalOpen = false
      this.selectedExercise = null
    }
  }
}
</script>

<style scoped>
.breathing-exercises {
  background: #14171F;
  box-sizing: border-box;
  transition: padding 0.3s ease;
}

/* AI Loading Overlay */
.ai-loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(20, 23, 31, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.ai-loading-content {
  text-align: center;
  color: #ffffff;
}

.spinner {
  width: 60px;
  height: 60px;
  border: 4px solid rgba(255, 255, 255, 0.1);
  border-top: 4px solid #ffffff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.ai-loading-text {
  font-size: 1.5rem;
  margin: 0 0 10px;
  font-weight: 500;
}

.ai-loading-subtext {
  font-size: 1rem;
  margin: 0;
  opacity: 0.7;
}

.page-title {
  text-align: center;
  color: #ffffff;
  font-weight: 400;
  transition: font-size 0.3s ease, margin-bottom 0.3s ease;
}

.loading,
.error {
  text-align: center;
  padding: 40px;
  font-size: 1.5rem;
  color: #ffffff;
}

.error {
  color: #e74c3c;
}

.courses-grid {
  transition: all 0.3s ease;
}

.course-item {
  opacity: 0;
  animation: fadeInUp 0.5s ease forwards;
  min-height: 0;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>